/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UserAuthRecord, UserRole } from '../types/auth';
import { User } from '../types';
import { auth } from '../utils/firebaseAuth';
import { getStoredSSOTAuthDoc } from '../utils/authAuthority';

export interface AuthoritativeContext {
  uid: string | null;
  email: string | null;
  companyId: string;
  role: UserRole;
  isActive: boolean;
  isSuperAdmin: boolean;
  isDirector: boolean;
  isRegente: boolean;
  isCalidad: boolean;
  isOperario: boolean;
}

/**
 * Resolves authoritative security context.
 * Single source of truth across the entire application.
 */
export function resolveAuthoritativeContext(
  currentUser?: User | null
): AuthoritativeContext {
  const currentAuth = auth.currentUser;
  const ssot = getStoredSSOTAuthDoc();
  
  const uid = currentAuth?.uid || ssot?.uid || null;
  const email = currentAuth?.email || ssot?.email || currentUser?.correo || null;
  
  // SuperAdmin detection (JUAN.VALENCIA / Master Email)
  const isSuperAdmin = 
    (email && email.toLowerCase() === 'qualityprocesosyservicios@gmail.com') ||
    (currentUser?.username?.toUpperCase() === 'JUAN.VALENCIA') ||
    (ssot?.role === 'superadmin');

  // Authoritative companyId derived from SSOT or fallback
  const companyId = isSuperAdmin && !ssot?.companyId 
    ? 'company-insumedic' 
    : (ssot?.companyId || currentUser?.companyId || 'company-insumedic');

  // Authoritative Role
  let role: UserRole = 'operario';
  if (isSuperAdmin) {
    role = 'superadmin';
  } else if (ssot?.role) {
    role = ssot.role;
  } else if (currentUser) {
    if (currentUser.esAdmin || currentUser.cargo?.toLowerCase().includes('director') || currentUser.cargo?.toLowerCase().includes('administrador')) {
      role = 'director_tecnico';
    } else if (currentUser.cargo?.toLowerCase().includes('regente')) {
      role = 'regente';
    } else if (currentUser.cargo?.toLowerCase().includes('calidad')) {
      role = 'calidad';
    } else {
      role = 'operario';
    }
  }

  const isActive = ssot ? ssot.estado === 'ACTIVO' : (currentUser?.estado !== 'INACTIVO');

  return {
    uid,
    email,
    companyId,
    role,
    isActive,
    isSuperAdmin: Boolean(isSuperAdmin),
    isDirector: role === 'director_tecnico' || Boolean(isSuperAdmin),
    isRegente: role === 'regente' || role === 'director_tecnico' || Boolean(isSuperAdmin),
    isCalidad: role === 'calidad' || role === 'director_tecnico' || Boolean(isSuperAdmin),
    isOperario: role === 'operario' || Boolean(isSuperAdmin)
  };
}
